import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Mail, CheckCircle, XCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const EmailTesting = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const addResult = (test, status, message) => {
    setResults(prev => [...prev, { test, status, message, timestamp: new Date().toLocaleTimeString() }]);
  };

  const testRegistration = async () => {
    setLoading(true);
    try {
      const testEmail = `test${Date.now()}@trapcomix.com`;
      const response = await axios.post(`${API}/auth/register`, {
        email: testEmail,
        password: 'TestPassword123!',
        role: 'artist'
      });

      addResult('Registration', 'success', `User created: ${testEmail}. Check console for verification email.`);
      toast.success('Registration test passed! Check backend logs for email.');
    } catch (error) {
      addResult('Registration', 'error', error.response?.data?.detail || error.message);
      toast.error('Registration test failed');
    }
    setLoading(false);
  };

  const testPasswordReset = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: 'test@trapcomix.com'
      });

      addResult('Password Reset', 'success', 'Reset email triggered. Check console for reset link.');
      toast.success('Password reset test passed! Check backend logs for email.');
    } catch (error) {
      addResult('Password Reset', 'error', error.response?.data?.detail || error.message);
      toast.error('Password reset test failed');
    }
    setLoading(false);
  };

  const testWelcomeEmail = async () => {
    addResult('Welcome Email', 'info', 'Welcome emails are sent automatically after email verification.');
    toast.info('Welcome emails trigger after verification');
  };

  const clearResults = () => {
    setResults([]);
  };

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-2">Email System Testing</h1>
          <p className="text-gray-400">Test authentication email flows (Mock Mode)</p>
          <Badge className="bg-yellow-500/10 text-yellow-500 mt-2">
            📧 Mock Mode Active - Check Backend Logs
          </Badge>
        </div>

        {/* Test Controls */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardHeader className="border-b border-gray-800">
            <h2 className="text-xl font-bold">Email Flow Tests</h2>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-4">
              <Button
                onClick={testRegistration}
                disabled={loading}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              >
                {loading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Test Registration
              </Button>

              <Button
                onClick={testPasswordReset}
                disabled={loading}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              >
                {loading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Test Password Reset
              </Button>

              <Button
                onClick={testWelcomeEmail}
                disabled={loading}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <Mail className="w-4 h-4 mr-2" />
                Test Welcome Email
              </Button>
            </div>

            {results.length > 0 && (
              <div className="mt-4">
                <Button
                  onClick={clearResults}
                  variant="outline"
                  size="sm"
                  className="border-gray-700"
                >
                  Clear Results
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardHeader className="border-b border-gray-800">
            <h2 className="text-xl font-bold">How to Check Email Logs</h2>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4 text-gray-300">
              <div>
                <h3 className="font-semibold text-white mb-2">Backend Console Logs:</h3>
                <code className="block bg-black p-3 rounded text-sm text-green-400">
                  tail -f /var/log/supervisor/backend.err.log | grep "MOCK EMAIL"
                </code>
              </div>

              <div>
                <h3 className="font-semibold text-white mb-2">What You'll See:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>From/To addresses</li>
                  <li>Email subject line</li>
                  <li>Content preview</li>
                  <li>Verification/reset links</li>
                </ul>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                <p className="text-orange-400 font-semibold mb-2">📌 Production Setup:</p>
                <p className="text-sm">
                  To enable real emails, add these to <code className="bg-black px-2 py-1 rounded">/app/backend/.env</code>:
                </p>
                <code className="block bg-black p-3 rounded mt-2 text-xs text-cyan-400">
                  SENDGRID_API_KEY=SG.your_api_key_here<br />
                  FROM_EMAIL=noreply@trapcomix.com
                </code>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Results */}
        {results.length > 0 && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="border-b border-gray-800">
              <h2 className="text-xl font-bold">Test Results</h2>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-800">
                {results.map((result, index) => (
                  <div key={index} className="p-4 hover:bg-gray-800/50 transition-colors">
                    <div className="flex items-start gap-3">
                      {result.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : result.status === 'error' ? (
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">{result.test}</h3>
                          <Badge
                            className={
                              result.status === 'success'
                                ? 'bg-green-500/10 text-green-500'
                                : result.status === 'error'
                                ? 'bg-red-500/10 text-red-500'
                                : 'bg-blue-500/10 text-blue-500'
                            }
                          >
                            {result.status}
                          </Badge>
                          <span className="text-xs text-gray-500">{result.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-400">{result.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email Flow Diagram */}
        <Card className="bg-gray-900 border-gray-800 mt-6">
          <CardHeader className="border-b border-gray-800">
            <h2 className="text-xl font-bold">Email Flow Overview</h2>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Registration Flow */}
              <div>
                <h3 className="font-semibold text-orange-500 mb-3">1️⃣ Registration Flow</h3>
                <div className="flex items-center gap-3 text-sm text-gray-300">
                  <div className="bg-gray-800 px-4 py-2 rounded">Sign Up</div>
                  <span>→</span>
                  <div className="bg-orange-500 px-4 py-2 rounded text-white">Verification Email</div>
                  <span>→</span>
                  <div className="bg-gray-800 px-4 py-2 rounded">Click Link</div>
                  <span>→</span>
                  <div className="bg-green-500 px-4 py-2 rounded text-white">Welcome Email</div>
                </div>
              </div>

              {/* Password Reset Flow */}
              <div>
                <h3 className="font-semibold text-blue-500 mb-3">2️⃣ Password Reset Flow</h3>
                <div className="flex items-center gap-3 text-sm text-gray-300">
                  <div className="bg-gray-800 px-4 py-2 rounded">Forgot Password</div>
                  <span>→</span>
                  <div className="bg-blue-500 px-4 py-2 rounded text-white">Reset Email</div>
                  <span>→</span>
                  <div className="bg-gray-800 px-4 py-2 rounded">Click Link</div>
                  <span>→</span>
                  <div className="bg-gray-800 px-4 py-2 rounded">New Password</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailTesting;
